import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Divider, Link, CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { brandConfig } from '../design/theme';

// Full-page sign-in for the customer console (the Anthropic/OpenAI pattern:
// the landing page only LINKS here; authentication happens on the console
// itself). Rendered by ProtectedRoute for signed-out visitors, so a
// successful sign-in simply re-renders the protected children in place —
// no redirect needed, and no redirect loop on the console subdomain.
export default function ConsoleSignIn() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('signin'); // signin | signup | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const handleMicrosoft = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { signInWithMicrosoft } = await import('../services/firebase');
      await signInWithMicrosoft();
    } catch (err) {
      setError(err?.message || 'Microsoft sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEmailSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (emailLoading) return;
    setError(null);
    setEmailLoading(true);
    try {
      const { signInWithEmail, signUpWithEmail, resetPassword } = await import('../services/firebase');
      if (mode === 'reset') {
        await resetPassword(email.trim());
        setMode('signin');
        setError('Reset link sent! Check your email.');
      } else if (mode === 'signup') {
        await signUpWithEmail(email.trim(), password, name.trim() || null);
        setMode('signin');
        setError('Account created! Check your email for a verification link, then sign in.');
      } else {
        await signInWithEmail(email.trim(), password);
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  }, [mode, email, password, name, emailLoading]);

  const busy = loading || emailLoading;

  return (
    <Box
      data-theme={theme.palette.mode}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        px: 2,
        // Warm, branded backdrop so the front door reads like the landing
        // page, not a barren form: soft rust glows in the corners + a faint
        // dot grid that fades out toward the edges.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          background: `
            radial-gradient(70% 60% at 50% -10%, ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.20)} 0%, transparent 64%),
            radial-gradient(52% 48% at 88% 112%, ${alpha(theme.palette.primary.main, isDark ? 0.20 : 0.15)} 0%, transparent 62%),
            radial-gradient(46% 46% at 2% 104%, ${alpha(theme.palette.primary.main, isDark ? 0.16 : 0.11)} 0%, transparent 62%)
          `,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, isDark ? 0.06 : 0.06)} 1px, transparent 1px)`,
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(circle at 50% 36%, #000 0%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 36%, #000 0%, transparent 80%)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 392,
          p: { xs: 3, sm: 4 },
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
          boxShadow: isDark
            ? `0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.06)}`
            : `0 24px 60px ${alpha(theme.palette.primary.main, 0.12)}, 0 2px 8px rgba(0,0,0,0.04)`,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              mb: 1.75,
              mx: 'auto',
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.32 : 0.2)}`,
            }}
          >
            <brandConfig.icon sx={{ fontSize: 26, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {mode === 'reset' ? 'Reset password' : mode === 'signup' ? 'Create your account' : 'Sign in to Restack'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {mode === 'reset'
              ? 'Enter your email to receive a reset link.'
              : 'Your console for seats, usage, and billing.'}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity={/Reset link sent|Account created/.test(error) ? 'success' : 'error'}
            onClose={() => setError(null)}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {mode !== 'reset' && (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleMicrosoft}
              disabled={busy}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.1 }}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                  <path fill="#f25022" d="M1 1h10v10H1z" />
                  <path fill="#00a4ef" d="M1 12h10v10H1z" />
                  <path fill="#7fba00" d="M12 1h10v10H12z" />
                  <path fill="#ffb900" d="M12 12h10v10H12z" />
                </svg>
              }
            >
              Continue with Microsoft
            </Button>

            <Divider sx={{ my: 2.5, fontSize: 12, color: 'text.secondary' }}>or</Divider>
          </>
        )}

        <Box component="form" onSubmit={handleEmailSubmit}>
          {mode === 'signup' && (
            <TextField
              size="small" placeholder="Full name" value={name}
              onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 1.25 }}
            />
          )}
          <TextField
            size="small" type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} required fullWidth sx={{ mb: 1.25 }}
          />
          {mode !== 'reset' && (
            <TextField
              size="small" type="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} required fullWidth sx={{ mb: 1.5 }}
            />
          )}
          <Button
            type="submit" variant="contained" disabled={busy} fullWidth disableElevation
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.1 }}
          >
            {emailLoading ? (
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
            ) : mode === 'reset' ? 'Send reset link' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          {mode === 'signin' ? (
            <>
              <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary"
                onClick={() => { setMode('signup'); setError(null); }}>
                Create account
              </Link>
              <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary"
                onClick={() => { setMode('reset'); setError(null); }}>
                Forgot password?
              </Link>
            </>
          ) : (
            <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary"
              onClick={() => { setMode('signin'); setError(null); }}>
              Back to sign in
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
}
